import type { QueryResponse } from "@unbox/api";
import { useCallback, useRef, useState } from "react";
import {
  formatChartValue,
  isChartType,
  numericSeries,
  pctChange,
  prettifyColumn,
  type ResolvedVisualization,
  selectValidVisualizations,
  toNumber,
} from "../lib/charts";
import Icon from "./Icon";
import { CreatorList, ResultsTableView } from "./ResultsTable";
import VideoPreviews from "./VideoPreviews";

// Chart palette — the mockup's purple scale (dark→light) and a distinct
// composition palette. The scale itself is defined in ask.css, which restates
// it lighter for the dark drawer.
const PURPLE_SCALE = [
  "var(--ask-chart-1)",
  "var(--ask-chart-2)",
  "var(--ask-chart-3)",
  "var(--ask-chart-4)",
  "var(--ask-chart-5)",
  "var(--ask-chart-6)",
];
const COMP_PALETTE = [
  "var(--ask-chart-3)",
  "var(--ask-chart-5)",
  "var(--ask-chart-6)",
  "var(--ask-chart-4)",
  "var(--ask-chart-1)",
  "var(--ask-chart-2)",
];

function CardShell({ title, right, children }: { title?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--ask-frame-bg, var(--color-bg-primary))",
        border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
        borderRadius: "var(--ask-frame-radius, 12px)",
        padding: 20,
        boxShadow: "var(--ask-frame-shadow, 0 1px 2px rgba(10,13,18,0.05))",
      }}
    >
      {(title || right) && (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {title ? (
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>{title}</div>
          ) : (
            <span />
          )}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  const up = delta >= 0;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: up ? "var(--color-success-700)" : "var(--color-error-700)",
        background: up ? "var(--color-success-50)" : "var(--color-error-50)",
        border: `1px solid ${up ? "var(--color-success-100)" : "var(--color-error-200)"}`,
        padding: "1px 7px",
        borderRadius: 9999,
      }}
    >
      {up ? "+" : "−"}
      {Math.abs(delta).toFixed(0)}%
    </span>
  );
}

function BarChart({ viz }: { viz: ResolvedVisualization }) {
  const cat = viz.categoryColumn!;
  const col = viz.valueColumns[0];
  const rows = viz.rows.map((r) => ({ label: String(r[cat] ?? ""), value: toNumber(r[col]) ?? 0 }));
  const max = Math.max(0, ...rows.map((r) => r.value));
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <CardShell
      title={viz.title}
      right={
        <span
          style={{
            fontSize: 12,
            color: "var(--color-text-quaternary)",
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatChartValue(total, viz.valueFormat, viz.unit)} total
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.map((r, i) => {
          const isMax = max > 0 && r.value === max;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 70,
                  flex: "none",
                  fontSize: 12,
                  textAlign: "right",
                  fontWeight: isMax ? 600 : 400,
                  color: isMax ? "var(--color-text-secondary)" : "var(--color-text-quaternary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.label}
              </div>
              <div style={{ flex: 1, height: 22, borderRadius: 5, background: "var(--color-gray-100)" }}>
                <div
                  style={{
                    width: `${max > 0 ? (r.value / max) * 100 : 0}%`,
                    height: "100%",
                    borderRadius: 5,
                    background: isMax ? "var(--color-brand-700)" : "var(--color-brand-300)",
                    minWidth: r.value > 0 ? 4 : 0,
                  }}
                />
              </div>
              <div
                style={{
                  width: 70,
                  flex: "none",
                  fontSize: 12,
                  textAlign: "right",
                  fontWeight: isMax ? 600 : 400,
                  color: isMax ? "var(--color-text-secondary)" : "var(--color-text-quaternary)",
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatChartValue(r.value, viz.valueFormat, viz.unit)}
              </div>
            </div>
          );
        })}
      </div>
    </CardShell>
  );
}

function LineChart({ viz }: { viz: ResolvedVisualization }) {
  const col = viz.valueColumns[0];
  const series = numericSeries(viz.rows, col);
  if (series.length < 2) return null;
  const cat = viz.categoryColumn;
  const W = 560,
    H = 180,
    padL = 8,
    padTop = 16,
    padBottom = 8;
  const max = Math.max(...series),
    min = Math.min(0, ...series);
  const span = max - min || 1;
  const x = (i: number) => padL + (i / (series.length - 1)) * (W - padL);
  const y = (v: number) => padTop + (1 - (v - min) / span) * (H - padTop - padBottom);
  const line = series.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${x(series.length - 1).toFixed(1)},${y(min).toFixed(1)} L${x(0).toFixed(1)},${y(min).toFixed(1)} Z`;
  const last = series[series.length - 1];
  const delta = pctChange(series[0], last);
  return (
    <CardShell
      title={viz.title}
      right={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: "var(--color-text-primary)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatChartValue(last, viz.valueFormat, viz.unit, true)}
          </span>
          {delta !== null && <DeltaBadge delta={delta} />}
        </span>
      }
    >
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {[0, 0.5, 1].map((t, k) => {
          const yy = padTop + t * (H - padTop - padBottom);
          return <line key={k} x1={padL} y1={yy} x2={W} y2={yy} stroke="var(--color-gray-100)" strokeWidth={1} />;
        })}
        <path d={area} fill="var(--color-brand-700)" fillOpacity={0.07} />
        <path
          d={line}
          fill="none"
          stroke="var(--color-brand-700)"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx={x(series.length - 1)}
          cy={y(last)}
          r={4}
          fill="var(--color-brand-700)"
          stroke="var(--color-bg-primary)"
          strokeWidth={2}
        />
      </svg>
      {cat && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "var(--color-text-quaternary)" }}>
            {String(viz.rows[0]?.[cat] ?? "")}
          </span>
          <span style={{ fontSize: 11, color: "var(--color-text-quaternary)" }}>
            {String(viz.rows[viz.rows.length - 1]?.[cat] ?? "")}
          </span>
        </div>
      )}
    </CardShell>
  );
}

function StackedBar({ viz }: { viz: ResolvedVisualization }) {
  const cat = viz.categoryColumn!;
  const col = viz.valueColumns[0];
  const segs = viz.rows.map((r) => ({ label: String(r[cat] ?? ""), value: toNumber(r[col]) ?? 0 }));
  const total = segs.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <CardShell title={viz.title}>
      <div style={{ display: "flex", height: 28, borderRadius: 6, overflow: "hidden", marginBottom: 16 }}>
        {segs.map((s, i) => (
          <div
            key={i}
            style={{ width: `${(s.value / total) * 100}%`, background: COMP_PALETTE[i % COMP_PALETTE.length] }}
          />
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {segs.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{ width: 10, height: 10, borderRadius: 3, background: COMP_PALETTE[i % COMP_PALETTE.length] }}
              />
              <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>{s.label}</span>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <span
                style={{
                  fontSize: 13,
                  color: "var(--color-text-quaternary)",
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatChartValue(s.value, viz.valueFormat, viz.unit)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--color-text-primary)",
                  fontVariantNumeric: "tabular-nums",
                  width: 42,
                  textAlign: "right",
                }}
              >
                {((s.value / total) * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </CardShell>
  );
}

function Funnel({ viz }: { viz: ResolvedVisualization }) {
  const cat = viz.categoryColumn!;
  const col = viz.valueColumns[0];
  const rows = viz.rows.map((r) => ({ label: String(r[cat] ?? ""), value: toNumber(r[col]) ?? 0 }));
  const first = rows[0]?.value || 1;
  return (
    <CardShell title={viz.title}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {rows.map((r, i) => {
          const prev = i > 0 ? rows[i - 1].value : null;
          const drop = prev && prev > 0 ? ((r.value - prev) / prev) * 100 : null;
          return (
            <div key={i}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--color-text-primary)",
                    display: "inline-flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.label}
                  {drop !== null && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--color-text-secondary)",
                        background: "var(--color-gray-100)",
                        border: "1px solid var(--color-border-secondary)",
                        padding: "1px 7px",
                        borderRadius: 9999,
                      }}
                    >
                      {drop >= 0 ? "+" : "−"}
                      {Math.abs(drop).toFixed(0)}%
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: "var(--color-text-secondary)",
                    fontFamily: "var(--font-mono)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatChartValue(r.value, viz.valueFormat, viz.unit)}
                </span>
              </div>
              <div style={{ height: 14, borderRadius: 5, background: "var(--color-gray-100)" }}>
                <div
                  style={{
                    width: `${first > 0 ? (r.value / first) * 100 : 0}%`,
                    height: "100%",
                    borderRadius: 5,
                    background: PURPLE_SCALE[Math.min(i, PURPLE_SCALE.length - 1)],
                    minWidth: r.value > 0 ? 6 : 0,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </CardShell>
  );
}

function Sparkline({ series, up }: { series: number[]; up: boolean }) {
  const W = 92,
    H = 34;
  const max = Math.max(...series),
    min = Math.min(...series);
  const span = max - min || 1;
  const x = (i: number) => (i / (series.length - 1)) * W;
  const y = (v: number) => H - 2 - ((v - min) / span) * (H - 4);
  const d = series.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" style={{ flexShrink: 0 }}>
      <path
        d={d}
        fill="none"
        stroke={up ? "var(--color-success-600)" : "var(--color-error-600)"}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MetricStrip({ viz }: { viz: ResolvedVisualization }) {
  const cards = viz.valueColumns.map((col) => {
    const series = numericSeries(viz.rows, col);
    const last = series.length ? series[series.length - 1] : null;
    const delta = series.length >= 2 ? pctChange(series[0], series[series.length - 1]) : null;
    return { col, label: prettifyColumn(col), series, last, delta };
  });
  return (
    <div>
      {viz.title && (
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 10 }}>
          {viz.title}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        {cards.map((c, i) => (
          <div
            key={i}
            style={{
              flex: "1 1 160px",
              minWidth: 150,
              background: "var(--ask-frame-bg, var(--color-bg-primary))",
              border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
              borderRadius: "var(--ask-frame-radius, 12px)",
              padding: "16px 18px",
              boxShadow: "0 1px 2px rgba(10,13,18,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "var(--color-text-quaternary)", marginBottom: 4 }}>{c.label}</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  color: "var(--color-text-primary)",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1,
                }}
              >
                {c.last !== null ? formatChartValue(c.last, viz.valueFormat, viz.unit, true) : "No data"}
              </div>
              {c.delta !== null && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginTop: 5,
                    color: c.delta >= 0 ? "var(--color-success-700)" : "var(--color-error-700)",
                  }}
                >
                  {c.delta >= 0 ? "+" : "−"}
                  {Math.abs(c.delta).toFixed(0)}%
                </div>
              )}
            </div>
            {c.series.length >= 2 && <Sparkline series={c.series} up={(c.delta ?? 0) >= 0} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricGrid({ viz }: { viz: ResolvedVisualization }) {
  const cells = viz.valueColumns.map((col) => {
    const series = numericSeries(viz.rows, col);
    return { label: prettifyColumn(col), value: series.length ? series[series.length - 1] : null };
  });
  return (
    <div>
      {viz.title && (
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 10 }}>
          {viz.title}
        </div>
      )}
      <div
        style={{
          display: "flex",
          background: "var(--ask-frame-bg, var(--color-bg-primary))",
          border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
          borderRadius: "var(--ask-frame-radius, 12px)",
          overflow: "hidden",
          boxShadow: "var(--ask-frame-shadow, 0 1px 2px rgba(10,13,18,0.05))",
        }}
      >
        {cells.map((c, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: "16px 18px",
              borderLeft: i > 0 ? "1px solid var(--color-border-secondary)" : "none",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: "var(--color-text-primary)",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.1,
              }}
            >
              {c.value !== null ? formatChartValue(c.value, viz.valueFormat, viz.unit, true) : "No data"}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-quaternary)", marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function rowFormColumns(viz: ResolvedVisualization): string[] {
  return [viz.categoryColumn, viz.detailColumn, ...viz.valueColumns].filter((c): c is string => !!c);
}

function renderChart(viz: ResolvedVisualization) {
  switch (viz.type) {
    case "bar":
      return <BarChart viz={viz} />;
    case "line":
      return <LineChart viz={viz} />;
    case "stacked_bar":
      return <StackedBar viz={viz} />;
    case "funnel":
      return <Funnel viz={viz} />;
    case "metric_strip":
      return <MetricStrip viz={viz} />;
    case "metric_grid":
      return <MetricGrid viz={viz} />;
    case "list":
      return <CreatorList columns={rowFormColumns(viz)} rows={viz.rows} />;
    case "table":
      return <ResultsTableView columns={rowFormColumns(viz)} rows={viz.rows} />;
    case "videos":
      return <VideoPreviews viz={viz} />;
    default:
      return null;
  }
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 60) || "chart"
  );
}

// Wraps a rendered chart with a hover "PNG" button that rasterises the chart
// node (the export library is loaded on demand, only when first used). The
// button sits in the ref'd node's own corner but is excluded from capture via
// the filter, so it never appears in the downloaded image.
function ExportableChart({ title, children }: { title: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = useCallback(async () => {
    const node = ref.current;
    if (!node || busy) return;
    setBusy(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: "var(--color-bg-primary)",
        cacheBust: true,
        filter: (n: HTMLElement) => !(n instanceof HTMLElement && n.dataset.exportExclude === "true"),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `ask-unbox-${slugify(title)}.png`;
      a.click();
    } catch {
      // best-effort: export failures should never disrupt the answer
    } finally {
      setBusy(false);
    }
  }, [busy, title]);

  return (
    <div
      ref={ref}
      style={{ position: "relative" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      <button
        type="button"
        onClick={download}
        data-export-exclude="true"
        title="Download as PNG"
        aria-label="Download chart as PNG"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          height: 26,
          padding: "0 8px",
          borderRadius: 6,
          border: "1px solid var(--color-border-secondary)",
          background: "var(--color-bg-primary)",
          color: "var(--color-text-quaternary)",
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "inherit",
          cursor: busy ? "wait" : "pointer",
          opacity: hover ? 1 : 0,
          transition: "opacity 120ms",
          boxShadow: "0 1px 2px rgba(10,13,18,0.05)",
        }}
      >
        <Icon name="download" size={12} color="currentColor" strokeWidth={2} />
        PNG
      </button>
    </div>
  );
}

export default function Visualizations({ response }: { response: Pick<QueryResponse, "steps" | "visualizations"> }) {
  const vizes = selectValidVisualizations(response);
  if (vizes.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 14 }}>
      {vizes.map((v, i) =>
        isChartType(v.type) ? (
          <ExportableChart key={i} title={v.title ?? v.type}>
            {renderChart(v)}
          </ExportableChart>
        ) : (
          <div key={i}>{renderChart(v)}</div>
        ),
      )}
    </div>
  );
}
